import React from "react";

import { GanttChartConfigProps, IGanttRange } from "components/ComponentType";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { IProductTemplate } from "components/dashboard/product-template-table/utils/interfaces";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 as uuidv4 } from "uuid";
import { AttributeType } from "@energy-advice/easas-api";

type GanttRangesProps = GanttChartConfigProps & {
  data: IProductTemplate | undefined;
};

const GanttRanges: React.FC<GanttRangesProps> = ({ data, currentConfig, setCurrentConfig }) => {
  const handleRangeChange = (
    body: IGanttRange,
    e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent
  ) => {
    setCurrentConfig(prev => ({
      ...prev,
      ganttRanges: prev.ganttRanges.map(ob =>
        ob.id === body.id ? { ...ob, [e.target.name]: e.target.value } : ob
      ),
    }));
  };

  const renderTitleInput = (body: IGanttRange) => (
    <InputText
      value={body.title ?? ""}
      type="text"
      className="p-inputtext-sm w-14rem"
      placeholder={T("action.write-title")}
      name="title"
      onChange={e => handleRangeChange(body, e)}
    />
  );

  const renderDropdown = (body: IGanttRange, field: keyof IGanttRange) => (
    <Dropdown
      value={body[field] ?? ""}
      options={data?.data?.attributes.filter(attr => attr.type !== AttributeType.DATETIME)}
      optionLabel="displayName"
      optionValue="referenceName"
      placeholder={T("word.select-one")}
      name={field}
      className="p-inputtext-sm w-14rem"
      onChange={e => handleRangeChange(body, e)}
    />
  );

  const renderColorPicker = (body: IGanttRange) => (
    <ColorPicker
      name="color"
      value={body.color}
      className="p-inputtext-sm w-full"
      onValueChange={e => handleRangeChange(body, e)}
    />
  );

  const deleteBtn = (body: IGanttRange) => (
    <Button
      type="button"
      icon="pi pi-trash"
      className="p-button-danger p-button-text p-button-rounded p-button-raised"
      onClick={() =>
        setCurrentConfig(prev => ({
          ...prev,
          ganttRanges: prev.ganttRanges.toSpliced(
            prev.ganttRanges.findIndex(ob => ob.id === body.id),
            1
          ),
        }))
      }
    />
  );

  const handleCreateNewRange = () => {
    const initialRange: IGanttRange = {
      id: uuidv4(),
      color: "#000000",
      endDateColumn: null,
      startDateColumn: null,
      title: null,
    };
    setCurrentConfig(prev => ({
      ...prev,
      ganttRanges: [...prev.ganttRanges, initialRange],
    }));
  };

  return (
    <>
      <DataTable value={currentConfig.ganttRanges}>
        <Column field="title" header={T("word.title")} className="p-1" body={renderTitleInput} />
        <Column
          field="startDateColumn"
          header={T("word.start-date")}
          className="p-1"
          body={body => renderDropdown(body, "startDateColumn")}
        />
        <Column
          field="endDateColumn"
          header={T("word.end-date")}
          className="p-1"
          body={body => renderDropdown(body, "endDateColumn")}
        />
        <Column field="color" header={T("word.color")} className="p-1" body={renderColorPicker} />
        <Column body={deleteBtn} />
      </DataTable>
      <div className="flex mt-3">
        <Button
          type="button"
          icon="pi pi-plus"
          className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
          title={T("action.add")}
          onClick={handleCreateNewRange}
        />
      </div>
    </>
  );
};

export default GanttRanges;
